import React, { FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Copy, { CopyProps } from 'Src/components/Copy';
import { Check } from 'Src/feature/check/models';

import styles from './CheckTotalEntry.module.scss';

type CheckTotalEntryEntryType = 'discount' | 'subtotal' | 'tax' | 'tip' | 'total' | 'overpaid' | 'fees' | 'gratuities' | 'balance';

interface CheckTotalEntryProps {
  type: CheckTotalEntryEntryType;
  check: Check | undefined;
  align?: {
    name?: CopyProps['align'],
    value?: CopyProps['align'],
  }
}

const CheckTotalEntry: FC<CheckTotalEntryProps> = ({
  type,
  check,
  align,
}) => {
  const { t } = useTranslation();

  const nameProps: CopyProps = {
    ['data-testid']: `check-total-name-${type}`,
    value: '',
    variant: 'body',
    color: 'textSecondary',
    align: align?.name,
  };

  const valueProps: CopyProps = {
    ['data-testid']: `check-total-value-${type}`,
    value: 0,
    variant: 'body',
    color: 'textSecondary',
    type: 'currency',
    align: align?.value,
  };

  switch (type) {
    case 'discount':
      if (check?.balance?.discount === 0) return null;
      nameProps.value = t('check.labels.balance.discounts');
      valueProps.value = check?.balance?.discount ?? 0;
      valueProps.negative = true;
      break;
    case 'subtotal':
      nameProps.value = t('check.labels.balance.subtotal');
      valueProps.value = check?.balance?.subtotal ?? 0;
      break;
    case 'tax':
      nameProps.value = t('check.labels.balance.tax');
      valueProps.value = check?.balance?.tax ?? 0;
      break;
    case 'tip':
      nameProps.value = t('check.labels.balance.tip');
      valueProps.value = check?.balance?.tip ?? 0;
      break;
    case 'total':
      nameProps.value = t('check.labels.balance.total');
      nameProps.color = 'textPrimary';
      valueProps.value = check?.balance?.total ?? 0;
      valueProps.size = 'lg';
      valueProps.color = 'textPrimary';
      break;
    case 'overpaid':
      nameProps.value = t('check.labels.balance.overpaid');
      nameProps.color = 'error';
      valueProps.value = check?.balance.overpaid ?? 0;
      valueProps.size = 'lg';
      valueProps.color = 'error';
      break;
    case 'fees':
      if (check?.balance?.fees === 0) return null;
      nameProps.value = t('check.labels.balance.fees');
      valueProps.value = check?.balance?.fees ?? 0;
      valueProps.positive = true;
      break;
    case 'gratuities':
      if (check?.balance?.gratuities === 0) return null;
      nameProps.value = t('check.labels.balance.gratuities');
      valueProps.value = check?.balance?.gratuities ?? 0;
      valueProps.positive = true;
      break;
    case 'balance':
      nameProps.value = t('check.labels.balance.balance');
      nameProps.color = 'textPrimary';
      return (
        <>
          <Copy {...nameProps} />
          <Trans
            i18nKey="check.labels.balance.balanceValue"
            parent="div"
            className={styles.balance}
            context={check?.balance?.cashDiscountedTotal !== undefined ? 'cashDiscountAvailable': ''}
            values={{
              balance: check?.balance?.unpaid ?? 0,
              cashBalance: check?.balance?.cashDiscountedTotal,
            }}
            components={{
              balance: <Copy color="textPrimary" size="lg" />,
              cashBalance: <Copy color="textSecondary" size="lg" />,
            }}  
          />
        </>
      );
    default:
      return null;
  }

  return (
    <>
      <Copy {...nameProps} />
      <Copy {...valueProps} />
    </>
  );
};

export { CheckTotalEntry, CheckTotalEntryEntryType };
